import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  Tab,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core';
import { Search, Clear, Delete as DeleteIcon, Add } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';
import { useLazyQuery, useMutation } from "@apollo/client";
import { ApolloError } from "apollo-boost";
import { IAppState } from '../../../../store';
import { StyledTableCell, StyledTableRow } from '../../../StyledTable';
import ProfileScreen from '../../../Profile';
import { DELETE_DRIVER, DELETE_DRIVER_DESCRIPTION, IConfirmationDialogData, defaultConfirmationDialogData } from "../../Summary/const";
import { DELETE_BUSINESS_CUSTOMER_DRIVER } from "../../../../graphql/user/updateUser";
import { SnackBarVariant } from "../../../SnackbarWrapper/SnackbarWrapper";
import { useSnackBar } from "../../../SnackBarContext/SnackBarContext";
import { formatGraphQLErrorMessage } from "../../../utils";
import { ConfirmationDialog } from "../../../ConfirmationDialog/ConfirmationDialog";
import { useTranslation } from "react-i18next";
import { GET_VEHICLES_FOR_BUSINESS_CUSTOMER } from "../../../../graphql/vehicles/getVehicles";
import { UserRole } from "../../../../reducers/auth/types";

const useStyles = makeStyles((theme) => ({
  chip: {
    fontSize: "12px",
    height: "24px",
    borderRadius: "12px",
    fontWeight: 500
  },
  filterCard: {
    padding: "16px",
    marginBottom: "16px",
    backgroundColor: "#f5f5f5"
  },
  tabs: {
    marginBottom: "16px",
    backgroundColor: "#fff",
    borderRadius: "4px"
  }
}));

interface LocationState {
  showAvailableDrivers?: boolean;
}

const Drivers = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const snackbar = useSnackBar();
  const { id: idFromURL } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const user = useSelector((state: IAppState) => state.authReducer.user);  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);  
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [tabValue, setTabValue] = useState(0);  
  const [sortBy, setSortBy] = useState<string>("firstName");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");  
  const [driverId, setDriverId] = useState<string | undefined>(idFromURL);
  const [confirmationDialogData, setConfirmationDialogData] = useState<
    IConfirmationDialogData
  >(defaultConfirmationDialogData);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput);
        setPage(0);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const [getVehicles, { loading: vehiclesLoading, data: vehiclesData, error }] =
    useLazyQuery(GET_VEHICLES_FOR_BUSINESS_CUSTOMER, {
      fetchPolicy: "cache-and-network",
    });

  const [deleteBusinessCustomerDriver] = useMutation(
    DELETE_BUSINESS_CUSTOMER_DRIVER,
    {
      fetchPolicy: "no-cache",
      onCompleted: (data: any) => {
        snackbar({
          message: t("snackBar:businessCustomerDriverDeleted"),
          variant: SnackBarVariant.SUCCESS
        });
        setConfirmationDialogData({
          ...confirmationDialogData,
          isOpen: false,
          isLoading: false
        })
        window.location.reload();
      },
      onError: (error: ApolloError) =>
        snackbar({
          message: formatGraphQLErrorMessage(error.message),
          variant: SnackBarVariant.ERROR
        })
    }
  );

  useEffect(() => {
    if(idFromURL){
      setDriverId(idFromURL)
    }
    else{
      const pathname = window.location.pathname;
      const idFromUrl = pathname.split('/').pop();
      setDriverId(idFromUrl)
    }
    }, [location])

  useEffect(() => {
    if (location.state?.showAvailableDrivers && user && user.businesscustomers) {
      getVehicles({
        variables: {
          businessCustomerId: user.businesscustomers.id,
          ...(user.role === UserRole.DRIVER && { driver: user.customerId }),
        },
      });
    }
  }, [location.state, getVehicles, user]);

  const assignedDriverIds = useMemo(() =>
    vehiclesData?.getVehiclesForBusinessCustomer
      ?.map((v: any) => v.activeBookingSchedule?.driver?.id)
      .filter(Boolean) || [],
    [vehiclesData]
  );

  const availableDrivers = useMemo(() =>
    user?.businesscustomers?.approvedDrivers?.filter(
      d => d && !assignedDriverIds.includes(d?.id)
    ) || [],
    [user, assignedDriverIds]
  );

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  }, []);

  const handleTabChange = useCallback((event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  }, []);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleRequestSort = useCallback((property: string) => {
    const isAsc = sortBy === property && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortBy(property);
  }, [sortBy, sortOrder]);

  const handleClearFilters = useCallback(() => {
    setSearchInput("");
    setSearchTerm("");
    setPage(0);
  }, []);

  const getFilteredDrivers = () => {
    let filtered = [...availableDrivers];

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((driver) =>
        driver?.firstName?.toLowerCase().includes(searchLower) ||
        driver?.lastName?.toLowerCase().includes(searchLower) ||
        driver?.email?.toLowerCase().includes(searchLower)
      );
    }

    filtered.sort((a, b) => {
      if (!a || !b) return 0;
      const aValue = (a as any)[sortBy];
      const bValue = (b as any)[sortBy];
      if (!aValue) return 1;
      if (!bValue) return -1;

      const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortOrder === "asc" ? comparison : -comparison;
    });

    return filtered;
  };

  const filteredDrivers = getFilteredDrivers();
  const totalCount = filteredDrivers.length;
  const paginatedDrivers = filteredDrivers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (!user?.businesscustomers) {
    return <></>
  }

  const selectedDriver = user.businesscustomers?.approvedDrivers?.find(driver => driver?.id === driverId);

  return (
    <Switch>
      <Route path="/account/drivers" exact>
        <div style={{ padding: "24px" }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: "24px" }}>
            <Typography variant="h4" style={{ fontWeight: 600, fontSize: "2rem" }}>
              {t("root:drivers")}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => history.push(`/account/drivers/new`)}
              style={{
                backgroundColor: "#1976d2",
                color: "#fff",
                textTransform: "none",
                fontWeight: 600
              }}
            >
              {t("root:add")}
            </Button>
          </Box>

          <Card className={classes.filterCard}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={9}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by name or email..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                    endAdornment: searchInput && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSearchInput("");
                            setSearchTerm("");
                          }}
                          edge="end"
                        >
                          <Clear />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Button fullWidth variant="outlined" startIcon={<Clear />} onClick={handleClearFilters}>
                  Clear All
                </Button>
              </Grid>
            </Grid>
          </Card>

          <Box style={{ position: "relative" }}>
            <TableContainer style={{ maxHeight: "600px" }}>
              <Table stickyHeader aria-label="drivers table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>
                      <TableSortLabel
                        active={sortBy === "firstName"}
                        direction={sortBy === "firstName" ? sortOrder : "asc"}
                        onClick={() => handleRequestSort("firstName")}
                      >
                        {t("root:firstNameLabel")}
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell>
                      <TableSortLabel
                        active={sortBy === "lastName"}
                        direction={sortBy === "lastName" ? sortOrder : "asc"}
                        onClick={() => handleRequestSort("lastName")}
                      >
                        {t("root:lastNameLabel")}
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell>
                      <TableSortLabel
                        active={sortBy === "email"}
                        direction={sortBy === "email" ? sortOrder : "asc"}
                        onClick={() => handleRequestSort("email")}
                      >
                        {t("root:email")}
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell></StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedDrivers.length > 0 ? (
                    paginatedDrivers.map((driver, index) =>
                      driver && (
                        <StyledTableRow
                          key={driver.id || index}
                          onClick={() => {
                            setDriverId(driver.id);
                            history.push(`/account/drivers/${driver.id}`);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          <StyledTableCell>{driver.firstName}</StyledTableCell>
                          <StyledTableCell>{driver.lastName}</StyledTableCell>
                          <StyledTableCell>{driver.email}</StyledTableCell>
                          <StyledTableCell>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmationDialogData({
                                  description: t(`root:driverActions.${DELETE_DRIVER_DESCRIPTION}`),
                                  isOpen: true,
                                  onCancel: () =>
                                    setConfirmationDialogData({
                                      ...confirmationDialogData,
                                      isOpen: false
                                    }),
                                  onConfirm: () => {
                                    setConfirmationDialogData({
                                      ...confirmationDialogData,
                                      description: t(`root:driverActions.${DELETE_DRIVER_DESCRIPTION}`),
                                      title: t(`root:driverActions.${DELETE_DRIVER}`),
                                      isLoading: true,
                                      isOpen: true
                                    })
                                    deleteBusinessCustomerDriver({
                                      variables: {
                                        businessCustomerId: user.businesscustomers?.id,
                                        driverId: driver.id
                                      }
                                    });
                                  },
                                  title: t(`root:driverActions.${DELETE_DRIVER}`),
                                })
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </StyledTableCell>
                        </StyledTableRow>
                      )
                    )
                  ) : (
                    <TableRow>
                      <StyledTableCell colSpan={4} align="center">
                        <Typography style={{ opacity: 0.5, padding: "40px 0" }}>
                          {t("root:noDriverFound")}
                        </Typography>
                      </StyledTableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onChangePage={handleChangePage}
              rowsPerPage={rowsPerPage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 100]}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    style: {
                      marginTop: "auto"
                    },
                  },
                },
              }}
            />
          </Box>

          <ConfirmationDialog {...confirmationDialogData} />
        </div>
      </Route>
      <Route path="/account/drivers/:id">
        {selectedDriver && <ProfileScreen customer={selectedDriver} />}
      </Route>
    </Switch>
  )
}

export default Drivers